import { useState, useRef, useEffect, useCallback } from "react";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";

function Bubble({ role, body }) {
  const isPenny = role === "penny";
  return (
    <div className={`flex ${isPenny ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-line ${
        isPenny ? "bg-white border border-slate-200 text-slate-700 rounded-tl-sm"
                : "bg-charcoal text-white rounded-tr-sm"}`}>
        {body}
      </div>
    </div>
  );
}

export default function PennyWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const scrollRef = useRef(null);

  const scrollDown = () => setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 60);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal/penny/history");
      setMessages(data.messages || []);
      setSuggestions(data.suggestions || []);
    } catch { /* silent */ } finally { setLoaded(true); scrollDown(); }
  }, []);

  useEffect(() => { if (open && !loaded) loadHistory(); }, [open, loaded, loadHistory]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: `c${Date.now()}`, role: "client", body }]);
    setBusy(true); scrollDown();
    try {
      const { data } = await portalApi.post("/portal/penny", { message: body });
      setMessages((m) => [...m, { id: data.message_id, role: "penny", body: data.reply }]);
      setShowEscalate(!!data.escalate_suggested);
      if (data.suggestions) setSuggestions(data.suggestions);
    } catch {
      toast.error("Penny is unavailable right now.");
    } finally { setBusy(false); scrollDown(); }
  };

  const escalate = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/penny/escalate", { message: input.trim() || "I'd like to speak with my advisor." });
      setMessages((m) => [...m, { id: `e${Date.now()}`, role: "penny", body: data.reply }]);
      setShowEscalate(false); setInput("");
      toast.success("Your advisor has been notified.");
    } catch {
      toast.error("Could not reach your advisor. Please try the Messages page.");
    } finally { setBusy(false); scrollDown(); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="penny-launcher"
          className="fixed bottom-20 right-5 z-50 flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-dark text-charcoal font-semibold rounded-full pl-3 pr-4 py-3 shadow-lg shadow-amber-500/30 transition-transform hover:scale-105">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-charcoal">
            <Icons.Sparkles className="h-4 w-4 text-brand-gold" />
          </span>
          Ask Penny
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 right-5 z-50 w-[92vw] max-w-sm h-[34rem] max-h-[80vh] bg-[#F6F7FB] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="penny-panel">
          <div className="bg-charcoal text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full bg-brand-gold flex items-center justify-center"><Icons.Sparkles className="h-4 w-4 text-charcoal" /></div>
              <div className="leading-tight">
                <div className="font-semibold text-sm">Penny</div>
                <div className="text-[11px] text-slate-400">Client Success Coordinator</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="penny-close" className="p-1.5 rounded-lg hover:bg-white/10 text-slate-300"><Icons.X className="h-4 w-4" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3.5 space-y-3 tf-scroll" data-testid="penny-thread">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 mt-6 px-4">
                <div className="h-12 w-12 rounded-full bg-brand-gold/15 flex items-center justify-center mx-auto mb-2"><Icons.Sparkles className="h-6 w-6 text-brand-gold" /></div>
                <p className="text-sm font-medium text-slate-700">Hi, I'm Penny 👋</p>
                <p className="text-xs mt-1">I can help with your documents, organizer, status updates, or connect you to your advisor.</p>
              </div>
            )}
            {messages.map((m) => <Bubble key={m.id} role={m.role} body={m.body} />)}
            {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-2"><Icons.Loader2 className="h-4 w-4 animate-spin text-brand-gold" /></div></div>}
          </div>

          {showEscalate && (
            <div className="px-3.5 pb-2">
              <button onClick={escalate} disabled={busy} data-testid="penny-escalate"
                className="w-full flex items-center justify-center gap-2 bg-charcoal hover:bg-graphite text-white text-sm font-medium rounded-lg py-2.5">
                <Icons.LifeBuoy className="h-4 w-4 text-brand-gold" /> Contact my advisor
              </button>
            </div>
          )}

          {messages.length <= 1 && suggestions.length > 0 && (
            <div className="px-3.5 pb-2 flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} disabled={busy}
                  className="text-[11px] bg-white border border-slate-200 hover:border-brand-gold text-slate-600 rounded-full px-2.5 py-1">{s}</button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="penny-input" placeholder="Type a message…"
                className="flex-1 bg-[#F6F7FB] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40" />
              <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="penny-send"
                className="h-9 w-9 flex items-center justify-center rounded-lg bg-brand-gold hover:bg-brand-gold-dark disabled:opacity-50 text-charcoal"><Icons.Send className="h-4 w-4" /></button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">Penny is an automated assistant. A staff member will follow up for anything she can't handle.</p>
          </div>
        </div>
      )}
    </>
  );
}
